import type { ReactElement } from 'react';

import styles from './themes.module.css';

import { ChevronCircle, Minus, MinusAlt, Plus, PlusAlt } from '../../icons';
import { Color } from '../../styles';
import type { IconProps } from '../IconSvg';
import type { TextProps } from '../Text';

export enum AccordionVariant {
  /** A chickpea color-based variant. */
  'Chickpea' = 'Chickpea',
  /** A kale color-based variant. */
  'Kale' = 'Kale',
  /** A Blueberry color-based variant. */
  'Blueberry' = 'Blueberry',
  /** The default variant with big headings. */
  'Default' = 'Default',
  /** Foundation design language variant with outline circle chevron. */
  'Foundation' = 'Foundation',
}

export type AccordionTheme = {
  /**
   * The class name for the root element. Use the class name as base selector
   * when creating the theme CSS.
   */
  className: string;
  /** The {@link TextProps} for the heading. */
  headingProps: TextProps;
  /**
   * The icons for the heading, as a tuple:
   *
   * ```
   * [collapsed: <IconA />, expanded: <IconB />]
   * ```
   */
  headingIcons: [
    collapsed: ReactElement<IconProps>,
    expanded: ReactElement<IconProps>,
  ];
};

export type AccordionThemes = {
  [key in AccordionVariant]: AccordionTheme;
};

const foundationHeadingProps: TextProps = {
  color: 'Kale3',
  variant: 'heading-16',
};

const foundationTheme: AccordionTheme = {
  className: styles.Foundation,
  headingProps: foundationHeadingProps,
  headingIcons: [
    <ChevronCircle key="collapsed" orientation="down" size={44} />,
    <ChevronCircle key="expanded" orientation="up" size={44} />,
  ],
};

const chickpeaTheme: AccordionTheme = {
  className: styles.Chickpea,
  headingProps: {
    color: 'Charcoal2',
    variant: 'heading-16',
    bold: true,
  },
  headingIcons: [
    <PlusAlt key="plus" baseColor={Color.Carrot2} size={16} />,
    <MinusAlt key="minus" baseColor={Color.Carrot2} size={16} />,
  ],
};

const kaleTheme: AccordionTheme = {
  className: styles.Kale,
  headingProps: {
    color: 'Kale2',
    variant: 'heading-16',
    bold: true,
  },
  headingIcons: [
    <PlusAlt key="plus" baseColor={Color.Kale2} size={16} />,
    <MinusAlt key="minus" baseColor={Color.Kale2} size={16} />,
  ],
};

const blueberryTheme: AccordionTheme = {
  className: styles.Blueberry,
  headingProps: {
    color: 'Blueberry3',
    variant: 'heading-12',
    bold: true,
  },
  headingIcons: [
    <PlusAlt key="plus" baseColor={Color.Blueberry3} size={16} />,
    <MinusAlt key="minus" baseColor={Color.Blueberry3} size={16} />,
  ],
};

const defaultTheme: AccordionTheme = {
  className: styles.Default,
  headingProps: {
    color: 'Charcoal3',
    variant: 'heading-22',
  },
  headingIcons: [
    <Plus key="plus" size={36} baseColor={Color.Kale3} />,
    <Minus key="minus" size={36} baseColor={Color.Kale3} />,
  ],
};

/** A map of the themes for each Accordion variant. */
export const themes: AccordionThemes = {
  Chickpea: chickpeaTheme,
  Blueberry: blueberryTheme,
  Kale: kaleTheme,
  Default: defaultTheme,
  Foundation: foundationTheme,
};
